import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { apiError } from "@/lib/api";
import { TrialNotice } from "@/components/TrialNotice";
import { Cross, LogOut, Clock, CalendarOff, Users, BarChart3, Settings, ClipboardList, CalendarDays, FileBarChart, CreditCard, Wifi, WifiOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const navItems = (role) =>
  role === "owner"
    ? [
        { to: "/admin", label: "Panoramica", icon: BarChart3, id: "nav-admin" },
        { to: "/admin/report", label: "Report e Cartellini", icon: FileBarChart, id: "nav-report" },
        { to: "/admin/richieste", label: "Richieste", icon: CalendarOff, id: "nav-requests" },
        { to: "/admin/timbrature", label: "Timbrature", icon: ClipboardList, id: "nav-records" },
        { to: "/admin/registro", label: "Registro modifiche", icon: ClipboardList, id: "nav-audit" },
        { to: "/admin/turni", label: "Turni", icon: CalendarDays, id: "nav-schedules" },
        { to: "/admin/operatori", label: "Operatori", icon: Users, id: "nav-operators" },
        { to: "/admin/fatturazione", label: "Fatturazione", icon: CreditCard, id: "nav-billing" },
        { to: "/admin/impostazioni", label: "Impostazioni", icon: Settings, id: "nav-settings" },
      ]
    : [
        { to: "/dashboard", label: "Timbratura", icon: Clock, id: "nav-dashboard" },
        { to: "/cartellino", label: "Il mio cartellino", icon: FileBarChart, id: "nav-timesheet" },
        { to: "/assenze", label: "Assenze", icon: CalendarOff, id: "nav-absences" },
      ];

export const Layout = ({ children }) => {
  const { user, pharmacy, net, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-2 min-w-0">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <Cross className="h-6 w-6 shrink-0 text-emerald-600" strokeWidth={2.5} />
            <div className="min-w-0">
              <span className="font-head font-extrabold tracking-tight block leading-tight truncate">FarmaPresenze</span>
              <span data-testid="pharmacy-name" className="text-xs text-gray-600 truncate block">{pharmacy?.name}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-4 shrink-0">
            <span
              data-testid="network-badge"
              title={net.emergency ? "Accesso di emergenza 24h" : net.ip_allowed ? "Rete farmacia" : "Fuori rete"}
              className={`flex items-center gap-1.5 rounded-full px-2 sm:px-3 py-1 text-xs font-semibold ${
                net.ip_allowed ? "bg-emerald-500/10 text-emerald-700" : "bg-red-500/10 text-red-700"
              }`}
            >
              {net.ip_allowed ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}
              <span className="hidden md:inline">{net.emergency ? "Emergenza 24h" : net.ip_allowed ? "Rete farmacia" : "Fuori rete"}</span>
            </span>
            <div className="text-right min-w-0">
              <p data-testid="current-user-name" className="text-sm font-semibold leading-tight truncate max-w-[110px] sm:max-w-none">{user?.name}</p>
              <p className="text-xs text-gray-600">{user?.role === "owner" ? "Titolare" : "Operatore"}</p>
            </div>
            <button
              data-testid="logout-btn"
              onClick={async () => { try {await logout(); navigate("/");} catch(e) {toast.error(apiError(e));} }}
              aria-label="Esci"
              className="h-10 px-3 sm:px-4 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 flex items-center gap-2 shrink-0 transition-colors hover:bg-gray-100"
            >
              <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Esci</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8">
        <nav className="flex flex-wrap lg:flex-col gap-2 min-w-0 self-start">
          {navItems(user?.role).map(({ to, label, icon: Icon, id }) => (
            <NavLink
              key={to}
              to={to}
              end
              data-testid={id}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium whitespace-nowrap transition-colors duration-200 ${
                  isActive ? "bg-emerald-600 text-white" : "text-gray-700 hover:bg-white hover:shadow-sm"
                }`
              }
            >
              <Icon className="h-4 w-4" /> {label}
            </NavLink>
          ))}
        </nav>
        <main className="min-w-0 fade-up">{user?.role === "owner" && pharmacy?.access_enabled && location.pathname !== "/admin/fatturazione" && <TrialNotice />}{children}</main>
      </div>
    </div>
  );
};
